import React, { useState } from 'react';
import { ClipboardList, CheckCircle, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DEVICE_MODELS = [
  'NGX-100 Series',
  'NGX-200 Series',
  'NGX-PRO Series',
  'NGX-LITE Series',
  'Custom / Not Listed',
];

const ORDER_TYPES = [
  { value: 'custom-electrode', label: 'Custom Electrode Configuration' },
  { value: 'bulk-supplies', label: 'Bulk Supply Order (>50 units)' },
  { value: 'replacement-parts', label: 'Replacement Parts Request' },
  { value: 'device-upgrade', label: 'Device Upgrade / Trade-In' },
  { value: 'new-device', label: 'New Device Order' },
  { value: 'service-repair', label: 'Service / Repair Request' },
  { value: 'other', label: 'Other / Custom Request' },
];

export default function CustomOrderPage() {
  const { user, profile } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    orderType: '',
    deviceModel: '',
    quantity: '',
    priority: 'standard',
    specifications: '',
    notes: '',
    contactPreference: 'email',
    // Pre-fill from profile
    practiceName: profile?.practice_name || '',
    contactName: profile?.full_name || '',
    email: user?.email || '',
    phone: profile?.phone || '',
  });

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // ============================================================
    // FORM SUBMISSION — choose one approach:
    //
    // OPTION 1: Supabase (store in DB)
    // const { error } = await supabase.from('custom_orders').insert([{
    //   provider_id: user.id,
    //   ...form,
    //   created_at: new Date().toISOString(),
    //   status: 'submitted'
    // }]);
    //
    // OPTION 2: Email via EmailJS or Formspree
    // await fetch('https://formspree.io/f/YOUR_FORM_ID', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ ...form, providerEmail: user.email }),
    // });
    //
    // OPTION 3: Trigger GHL workflow via webhook
    // await fetch('https://api.gohighlevel.com/hooks/YOUR_HOOK', {
    //   method: 'POST',
    //   body: JSON.stringify(form),
    // });
    // ============================================================

    await new Promise(r => setTimeout(r, 1200)); // Simulated delay
    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    return (
      <>
        <div className="page-header">
          <div>
            <div className="page-title">Custom Order</div>
            <div className="page-subtitle">Request specialized products or configurations</div>
          </div>
        </div>
        <div className="page-body">
          <div style={{
            background: 'white', border: '1px solid #e2e8f0', borderRadius: 16,
            padding: '60px 40px', textAlign: 'center', maxWidth: 520, margin: '0 auto'
          }}>
            <div style={{
              width: 64, height: 64, background: '#ecfdf5', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle size={32} color="#21999d" />
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
              Order Request Submitted!
            </div>
            <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
              Your custom order request has been received. A Neurogenx representative will contact you within <strong>1–2 business days</strong> to confirm details and pricing.
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '14px 16px', textAlign: 'left', marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Order Summary</div>
              <div style={{ fontSize: 13, color: '#475569' }}>
                <strong>{ORDER_TYPES.find(t => t.value === form.orderType)?.label}</strong><br />
                {form.deviceModel && <span>Device: {form.deviceModel}<br /></span>}
                {form.quantity && <span>Quantity: {form.quantity}<br /></span>}
                Contact: {form.email}
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => setSubmitted(false)}>
              Submit Another Request
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Custom Order Request</div>
          <div className="page-subtitle">Submit requests for specialized configurations, bulk orders, or device services</div>
        </div>
      </div>

      <div className="page-body">
        <div className="alert alert-info mb-16">
          <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Custom orders are reviewed by our team. Standard items can be ordered directly from the <strong>Reorder Supplies</strong> page. Connect a form endpoint (Supabase / Formspree / GHL webhook) to activate email notifications.</span>
        </div>

        <form className="form-card" onSubmit={handleSubmit}>
          {/* ORDER DETAILS */}
          <div className="form-section-title">Order Details</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Order Type <span className="required">*</span></label>
              <select className="form-select" value={form.orderType} onChange={set('orderType')} required>
                <option value="">Select order type...</option>
                {ORDER_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Device Model</label>
              <select className="form-select" value={form.deviceModel} onChange={set('deviceModel')}>
                <option value="">Select model (if applicable)...</option>
                {DEVICE_MODELS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input
                type="number" min="1" className="form-input"
                placeholder="e.g. 10"
                value={form.quantity} onChange={set('quantity')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority} onChange={set('priority')}>
                <option value="standard">Standard (5–7 business days)</option>
                <option value="expedited">Expedited (2–3 business days, +fee)</option>
                <option value="urgent">Urgent (1 business day, call required)</option>
              </select>
            </div>

            <div className="form-group full">
              <label className="form-label">Specifications / Configuration Details <span className="required">*</span></label>
              <textarea
                className="form-textarea"
                placeholder="Describe your requirements in detail. Include sizes, quantities, configurations, special requirements, etc."
                value={form.specifications}
                onChange={set('specifications')}
                required
                style={{ minHeight: 120 }}
              />
            </div>

            <div className="form-group full">
              <label className="form-label">Additional Notes</label>
              <textarea
                className="form-textarea"
                placeholder="Any other information — delivery preferences, billing questions, etc."
                value={form.notes}
                onChange={set('notes')}
                style={{ minHeight: 80 }}
              />
            </div>
          </div>

          {/* CONTACT INFO */}
          <div className="divider" />
          <div className="form-section-title">Contact Information</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Practice Name <span className="required">*</span></label>
              <input
                type="text" className="form-input"
                value={form.practiceName} onChange={set('practiceName')} required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Name <span className="required">*</span></label>
              <input
                type="text" className="form-input"
                value={form.contactName} onChange={set('contactName')} required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email <span className="required">*</span></label>
              <input
                type="email" className="form-input"
                value={form.email} onChange={set('email')} required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel" className="form-input"
                value={form.phone} onChange={set('phone')}
                placeholder="(555) 000-0000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Preferred Contact Method</label>
              <select className="form-select" value={form.contactPreference} onChange={set('contactPreference')}>
                <option value="email">Email</option>
                <option value="phone">Phone Call</option>
                <option value="text">Text Message</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
              {loading ? (
                <>
                  <div className="loading-spinner" style={{ width: 16, height: 16, borderWidth: 2, margin: 0 }} />
                  Submitting...
                </>
              ) : (
                <><Send size={15} /> Submit Order Request</>
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setForm(prev => ({
              ...prev, orderType: '', deviceModel: '', quantity: '', specifications: '', notes: ''
            }))}>
              Clear Form
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
